<div class="drawer" [@.disabled]="disableAnimation" [@slideInOut]="open ? 'open' : 'closed'"
  (@slideInOut.done)="onAnimationDone($event)" data-testid="intervention-drawer">
  <div class="drawer-header">
    <h2>
      <mat-icon [fontIcon]="mode === 'add' ? 'add' : 'edit'"></mat-icon>
      {{ (mode === 'add' ? 'interventions.add' : 'interventions.edit') | translate }}
    </h2>

    <lib-button type="button" (click)="cancel()" icon="close" color="none" textColor="muted"
      ariaLabel="{{ 'form.close' | translate }}" data-testid="intervention-drawer-close">
    </lib-button>
  </div>

  <div class="referential-warning" *ngIf="referentialError()" data-testid="intervention-referential-warning">
    <mat-icon fontIcon="warning"></mat-icon>
    <span>{{ 'interventions.referential_error.' + referentialError() | translate }}</span>
  </div>

  <form class="drawer-form" #interventionForm="ngForm" (submit)="save()">
    <mat-form-field class="full-width" appearance="outline">
      <mat-label>{{ 'interventions.field.category' | translate }}</mat-label>
      <mat-select #firstField [(ngModel)]="form.category" name="category" (selectionChange)="onCategoryChanged()"
        required data-testid="intervention-field-category">
        <mat-option *ngFor="let c of categories" [value]="c">
          {{ c }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="full-width" appearance="outline">
      <mat-label>{{ 'interventions.field.subType' | translate }}</mat-label>
      <mat-select [(ngModel)]="form.subType" name="subType" [disabled]="!form.category" required
        (ngModelChange)="markDirty()" data-testid="intervention-field-subtype">
        <mat-option *ngFor="let s of subTypeOptions" [value]="s">
          {{ s }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="full-width" appearance="outline">
      <mat-label>{{ 'interventions.field.trackers' | translate }}</mat-label>
      <mat-select [(ngModel)]="form.trackers" name="trackers" multiple (selectionChange)="markDirty()"
        data-testid="intervention-field-trackers">
        <mat-option *ngFor="let t of trackersForSelect" [value]="t.uuid">{{ t.label }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="full-width" appearance="outline">
      <mat-label>{{ 'interventions.field.description' | translate }}</mat-label>
      <textarea matInput [(ngModel)]="form.description" name="description" rows="3"
        (ngModelChange)="markDirty()"></textarea>
    </mat-form-field>

    <!-- Searchable user picker: no ready-made "user autocomplete" component exists yet in the app
         (checked ui-library and other features) — lib-search-select is the closest reusable
         generic "search within a list" component, already used for risks/projects/properties
         elsewhere. To revisit with the team once a dedicated component is confirmed. -->
    <lib-search-select [items]="usersForSelect" [selectedItem]="form.applicant" valueKey="id" labelKey="fullName"
      [label]="'interventions.field.applicant'" [searchPlaceholder]="'form.search' | translate"
      (itemChanged)="onApplicantChanged($event)" data-testid="intervention-field-applicant">
    </lib-search-select>

    <lib-search-select [items]="usersForSelect" [selectedItem]="form.assignee" valueKey="id" labelKey="fullName"
      [label]="'interventions.field.assignee'" [searchPlaceholder]="'form.search' | translate"
      (itemChanged)="onAssigneeChanged($event)" data-testid="intervention-field-assignee">
    </lib-search-select>

    <mat-form-field class="full-width" appearance="outline">
      <mat-label>{{ 'interventions.field.dueDate' | translate }}</mat-label>
      <input matInput [matDatepicker]="duePicker" [(ngModel)]="form.dueDate" name="dueDate"
        (input)="onDueDateInput($event)" data-testid="intervention-field-duedate">
      <mat-datepicker-toggle matSuffix [for]="duePicker"></mat-datepicker-toggle>
      <mat-datepicker #duePicker></mat-datepicker>
    </mat-form-field>

    <div class="section-divider">
      <mat-divider></mat-divider>
      <span>{{ 'interventions.evaluation_section' | translate }}</span>
      <mat-divider></mat-divider>
    </div>

    <mat-form-field class="full-width" appearance="outline">
      <mat-label>{{ 'interventions.field.evaluationResult' | translate }}</mat-label>
      <mat-select [(ngModel)]="form.evaluation!.result" name="evaluationResult"
        (ngModelChange)="onEvaluationResultChanged($event)" data-testid="intervention-field-evaluation-result">
        <mat-option [value]="null">{{ 'form.none' | translate }}</mat-option>
        <mat-option *ngFor="let e of evaluationOptions" [value]="e">
          {{ 'interventions.evaluation.' + e | translate }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field class="full-width" appearance="outline">
      <mat-label>{{ 'interventions.field.evaluationDate' | translate }}</mat-label>
      <input matInput [matDatepicker]="evalPicker" [(ngModel)]="form.evaluation!.date" name="evaluationDate"
        (ngModelChange)="markDirty()" data-testid="intervention-field-evaluation-date">
      <mat-datepicker-toggle matSuffix [for]="evalPicker"></mat-datepicker-toggle>
      <mat-datepicker #evalPicker></mat-datepicker>
    </mat-form-field>

    <mat-form-field class="full-width" appearance="outline">
      <mat-label>{{ 'interventions.field.evaluationComment' | translate }}</mat-label>
      <textarea matInput [(ngModel)]="form.evaluation!.comment" name="evaluationComment" rows="2"
        (ngModelChange)="markDirty()"></textarea>
    </mat-form-field>

    <div class="save-error" *ngIf="saveError" role="alert" data-testid="intervention-save-error">
      <mat-icon fontIcon="error"></mat-icon>
      <span>{{ saveError }}</span>
    </div>

    <div class="drawer-actions">
      <lib-button *ngIf="mode === 'edit'" type="button" (click)="remove()" color="none" textColor="danger"
        icon="delete" text="{{ 'form.delete' | translate }}" data-testid="intervention-drawer-delete">
      </lib-button>

      <span class="spacer"></span>

      <lib-button type="button" (click)="cancel()" color="outline" icon="close" text="{{ 'form.cancel' | translate }}">
      </lib-button>
      <lib-button color="primary" icon="save" text="{{ 'form.save' | translate }}" type="submit" [loading]="saving"
        data-testid="intervention-drawer-save">
      </lib-button>
    </div>
  </form>
</div>
